import JSZip from "jszip";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import { getAliquotaINPS } from "../constants/aliquote";
import { getAliquotaSostitutiva } from "./fiscalEngine";

const BOM = "\uFEFF";

function formatCurrency(value) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(value || 0);
}

function formatDateIT(isoDate) {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
}

/**
 * Genera il riepilogo fiscale PDF
 */
function generaRiepilogoPDF(anno, dati) {
  const doc = new jsPDF();
  const { fatturato, incassato, imponibile, imposta, inps, netto, aliquota, inpsRate, coeff, numFatture, bolloTotale } = dati;

  let y = 20;
  const lineHeight = 8;

  doc.setFontSize(18);
  doc.text(`Riepilogo Fiscale ${anno}`, 20, y);
  y += lineHeight * 2;

  doc.setFontSize(10);
  doc.text(`Generato il ${new Date().toLocaleDateString("it-IT")}`, 20, y);
  y += lineHeight * 2;

  doc.setFontSize(12);
  doc.text("Riepilogo Fatturato", 20, y);
  y += lineHeight;

  doc.setFontSize(10);
  const righe = [
    [`Numero fatture emesse:`, `${numFatture}`],
    [`Fatturato totale:`, formatCurrency(fatturato)],
    [`Incassato:`, formatCurrency(incassato)],
    [`Da incassare:`, formatCurrency(fatturato - incassato)],
    [``, ``],
    [`Coefficiente redditività:`, `${Math.round(coeff * 100)}%`],
    [`Imponibile forfettario:`, formatCurrency(imponibile)],
    [``, ``],
    [`Aliquota imposta sostitutiva:`, `${aliquota}%`],
    [`Imposta sostitutiva stimata:`, formatCurrency(imposta)],
    [``, ``],
    [`Aliquota INPS:`, `${inpsRate}%`],
    [`Contributi INPS stimati:`, formatCurrency(inps)],
    [``, ``],
    [`Netto stimato:`, formatCurrency(netto)],
    [``, ``],
    [`Bollo su fatture > 77,47€:`, formatCurrency(bolloTotale)],
  ];

  righe.forEach(([label, value]) => {
    if (label === "" && value === "") {
      y += lineHeight / 2;
      return;
    }
    doc.text(label, 20, y);
    doc.text(value, 120, y);
    y += lineHeight;
  });

  y += lineHeight;
  doc.setFontSize(8);
  doc.text("Nota: i calcoli sono stime basate sui dati inseriti. Verificare con il proprio commercialista.", 20, y);

  return doc.output("arraybuffer");
}

/**
 * Genera CSV fatture
 */
function generaFattureCSV(anno, invoices) {
  const header = "Data emissione;Numero;Cliente;Importo lordo;Incassata;Data incasso;Scadenza;Note";
  const rows = invoices.map((inv) => {
    const amt = (inv.grossAmount || 0).toFixed(2).replace(".", ",");
    return [
      formatDateIT(inv.issueDate),
      inv.invoiceCode || "",
      (inv.clientName || "").replace(/;/g, ","),
      amt,
      inv.paid ? "Sì" : "No",
      inv.paid ? formatDateIT(inv.paymentDate) : "",
      formatDateIT(inv.dueDate),
      (inv.notes || "").replace(/;/g, ",").replace(/\n/g, " "),
    ].join(";");
  });

  return BOM + [header, ...rows].join("\n");
}

/**
 * Genera CSV bollo (fatture > 77.47€)
 */
function generaBolloCSV(anno, invoices) {
  const soglia = 77.47;
  const fattureConBollo = invoices.filter((inv) => (inv.grossAmount || 0) > soglia);
  const totaleBolli = fattureConBollo.length * 2; // 2€ per bollo

  const header = "Data emissione;Numero;Cliente;Importo;Bollo (€)";
  const rows = fattureConBollo.map((inv) => {
    const amt = (inv.grossAmount || 0).toFixed(2).replace(".", ",");
    return [
      formatDateIT(inv.issueDate),
      inv.invoiceCode || "",
      (inv.clientName || "").replace(/;/g, ","),
      amt,
      "2,00",
    ].join(";");
  });

  const footer = `;;Totale bolli;;${totaleBolli.toFixed(2).replace(".", ",")}`;

  return BOM + [header, ...rows, "", footer].join("\n");
}

/**
 * Genera CSV clienti
 */
function generaClientiCSV(clients) {
  const header = "Nome;Email;Telefono;P.IVA;Codice Fiscale;Indirizzo";
  const rows = clients.map((c) => {
    return [
      (c.name || "").replace(/;/g, ","),
      c.email || "",
      c.phone || "",
      c.vatNumber || "",
      c.fiscalCode || "",
      (c.address || "").replace(/;/g, ",").replace(/\n/g, " "),
    ].join(";");
  });

  return BOM + [header, ...rows].join("\n");
}

/**
 * Genera file LEGGIMI.txt
 */
function generaLeggimi(anno) {
  return `PACCHETTO EXPORT FORFY - ANNO ${anno}
======================================

Contenuto del pacchetto:

1. riepilogo_fiscale_${anno}.pdf
   Riepilogo dei dati fiscali dell'anno: fatturato, imposte, contributi INPS stimati.

2. fatture_${anno}.csv
   Elenco completo delle fatture emesse nell'anno, con stato pagamento e scadenze.
   Separatore: punto e virgola (;). Encoding: UTF-8 con BOM.

3. bollo_${anno}.csv
   Fatture soggette a imposta di bollo (importo > 77,47 €) con totale bolli dovuti.

4. clienti.csv
   Anagrafica dei clienti registrati.

NOTE:
- I file CSV possono essere aperti con Excel (File > Apri, selezionare separatore ;)
- I calcoli sono stime basate sui dati inseriti nell'app Forfy
- Verificare sempre i dati con il proprio commercialista

Generato il ${new Date().toLocaleDateString("it-IT")} con Forfy
`;
}

/**
 * Funzione principale: genera e scarica lo ZIP
 */
export async function esportaPerCommercialista({ anno, invoices, clients, fiscalSettings }) {
  // Filtra fatture per anno
  const fattureAnno = invoices.filter((inv) => {
    if (!inv.issueDate) return false;
    return new Date(inv.issueDate).getFullYear() === anno;
  });

  // Calcoli fiscali: aliquota dell'anno ESPORTATO, non di oggi. Questo numero
  // finisce nel PDF consegnato al commercialista.
  const aliquota = getAliquotaSostitutiva(fiscalSettings.pivaYearStart, anno);
  const coeff = fiscalSettings.profitabilityCoefficient;
  const inpsRate = getAliquotaINPS(fiscalSettings.inpsType, anno);

  const fatturato = fattureAnno.reduce((s, inv) => s + (Number(inv.grossAmount) || 0), 0);
  const incassato = fattureAnno.filter((i) => i.paid).reduce((s, inv) => s + (Number(inv.grossAmount) || 0), 0);
  const imponibile = fatturato * coeff;
  const imposta = imponibile * aliquota / 100;
  const inps = imponibile * inpsRate / 100;
  const netto = fatturato - imposta - inps;
  const bolloTotale = fattureAnno.filter((inv) => (inv.grossAmount || 0) > 77.47).length * 2;

  const dati = {
    fatturato, incassato, imponibile, imposta, inps, netto,
    aliquota, inpsRate, coeff,
    numFatture: fattureAnno.length,
    bolloTotale,
  };

  // Genera ZIP
  const zip = new JSZip();

  zip.file(`riepilogo_fiscale_${anno}.pdf`, generaRiepilogoPDF(anno, dati));
  zip.file(`fatture_${anno}.csv`, generaFattureCSV(anno, fattureAnno));
  zip.file(`bollo_${anno}.csv`, generaBolloCSV(anno, fattureAnno));
  zip.file(`clienti.csv`, generaClientiCSV(clients));
  zip.file(`LEGGIMI.txt`, generaLeggimi(anno));

  const blob = await zip.generateAsync({ type: "blob" });
  saveAs(blob, `forfy_export_${anno}.zip`);
}
